import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import StaffService from "../../services/Staff/StaffService";
import {
  DollarSign,
  Calendar,
  Search,
  RotateCw,
  X,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Check,
  Edit2
} from "lucide-react";
import { Modal } from "../ui/Modal";
import { Toast } from "../ui/Toast";
import { Button } from "../ui/Button";

function StaffTaxas() {
  const [corridas, setCorridas] = useState([]);
  const [taxasMap, setTaxasMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [corridaSelecionada, setCorridaSelecionada] = useState(null);
  const [taxaPkSelecionada, setTaxaPkSelecionada] = useState(null);
  const [valorTaxaReal, setValorTaxaReal] = useState("5.00");
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  const toastRef = useRef(null);
  const staffService = useRef(new StaffService()).current;

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Carrega todas as corridas
      const resCorridas = await staffService.getTodasCorridas();
      let listaCorridas = [];
      if (resCorridas.ok) {
        const data = await resCorridas.json();
        listaCorridas = Array.isArray(data) ? data : (data?.results || []);
        setCorridas(listaCorridas);
      }

      // 2. Carrega taxas gerais cadastradas (GET fin/taxas/ - list)
      try {
        const resTaxas = await staffService.getTaxasGerais();
        if (resTaxas.ok) {
          const dataTaxas = await resTaxas.json();
          const mapa = {};
          const lista = Array.isArray(dataTaxas) ? dataTaxas : (dataTaxas?.results || []);
          lista.forEach((t) => {
            const id = t.id_corrida || (typeof t.corrida === "object" ? t.corrida?.id : t.corrida) || t.evento;
            if (id) {
              mapa[id] = t;
            }
          });
          setTaxasMap(mapa);
        }
      } catch (errTaxas) {
        console.warn("Aviso ao carregar taxas gerais:", errTaxas);
      }
    } catch (error) {
      console.error("Erro ao carregar dados de taxas:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as corridas e taxas."
      });
    } finally {
      setLoading(false);
    }
  }, [staffService]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const abrirEdicaoTaxa = async (corrida) => {
    setCorridaSelecionada(corrida);
    const idCorrida = corrida.id_corrida || corrida.id;

    // Verifica se já tem no mapa
    const taxaExistente = taxasMap[idCorrida] || corrida.taxa_urbrace || corrida.taxa;
    if (taxaExistente && typeof taxaExistente === "object") {
      setTaxaPkSelecionada(taxaExistente.id || taxaExistente.pk || null);
      const val = taxaExistente.valor ?? taxaExistente.valor_fixo ?? taxaExistente.taxa ?? 5.00;
      setValorTaxaReal(String(val));
      setObservacao(taxaExistente.observacao || taxaExistente.descricao || "");
    } else if (typeof taxaExistente === "number") {
      setTaxaPkSelecionada(null);
      setValorTaxaReal(String(taxaExistente));
      setObservacao("");
    } else {
      // Consulta no endpoint específico da corrida
      try {
        const res = await staffService.getTaxaCorrida(idCorrida);
        if (res.ok) {
          const d = await res.json();
          setTaxaPkSelecionada(d?.id || d?.pk || null);
          const val = d.valor ?? d.valor_fixo ?? d.taxa ?? 5.00;
          setValorTaxaReal(String(val));
          setObservacao(d.observacao || "");
        } else {
          setTaxaPkSelecionada(null);
          setValorTaxaReal("5.00");
          setObservacao("");
        }
      } catch {
        setTaxaPkSelecionada(null);
        setValorTaxaReal("5.00");
        setObservacao("");
      }
    }

    setDialogVisible(true);
  };

  const salvarTaxa = async () => {
    if (!corridaSelecionada) return;
    const idCorrida = corridaSelecionada.id_corrida || corridaSelecionada.id;
    setSalvando(true);

    const numValor = parseFloat(String(valorTaxaReal).replace(",", ".")) || 5.0;

    const payload = {
      id_corrida: idCorrida,
      corrida: idCorrida,
      valor: numValor,
      valor_fixo: numValor,
      taxa: numValor,
      valor_taxa: numValor,
      tipo_taxa: "fixo_reais",
      observacao: observacao,
      descricao: observacao
    };

    try {
      const res = await staffService.salvarTaxaCorrida(idCorrida, payload, taxaPkSelecionada);
      if (res && (res.ok || res.status === 200 || res.status === 201)) {
        let novoPk = taxaPkSelecionada;
        try {
          const dadosRetornados = await res.json();
          if (dadosRetornados?.id || dadosRetornados?.pk) {
            novoPk = dadosRetornados.id || dadosRetornados.pk;
          }
        } catch {
          // Sem corpo JSON
        }

        toastRef.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: `Taxa da corrida "${corridaSelecionada.nome_corrida || corridaSelecionada.nome}" atualizada para ${Number(numValor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} por inscrição!`
        });

        // Atualiza estado local
        setTaxasMap((prev) => ({
          ...prev,
          [idCorrida]: {
            ...payload,
            id: novoPk,
            pk: novoPk
          }
        }));

        setDialogVisible(false);
      } else {
        toastRef.current?.show({
          severity: "error",
          summary: "Erro ao Salvar",
          detail: `Status retornado pelo servidor: ${res?.status || "desconhecido"}`
        });
      }
    } catch (error) {
      console.error("Erro ao salvar taxa:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Falha",
        detail: "Ocorreu um erro ao enviar a taxa para o servidor."
      });
    } finally {
      setSalvando(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      const cleanStr = String(val).split("T")[0];
      const parts = cleanStr.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return cleanStr;
    } catch {
      return String(val);
    }
  };

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const getTaxaExibicao = (corrida) => {
    const id = corrida.id_corrida || corrida.id;
    const taxa = taxasMap[id] || corrida.taxa_urbrace || corrida.taxa;
    if (taxa) {
      if (typeof taxa === "object") {
        const v = taxa.valor ?? taxa.valor_fixo ?? taxa.taxa;
        if (v !== undefined && v !== null) {
          return `${formatCurrency(v)} / inscrição`;
        }
      }
      if (typeof taxa === "number") {
        return `${formatCurrency(taxa)} / inscrição`;
      }
    }
    return "R$ 5,00 / inscrição (Padrão)";
  };

  // Filtragem
  const dadosFiltrados = useMemo(() => {
    if (!globalFilter.trim()) return corridas;
    const term = globalFilter.toLowerCase();
    return corridas.filter((c) =>
      (c.nome_corrida || c.nome || "").toLowerCase().includes(term) ||
      (c.organizador_nome || c.organizador || "").toLowerCase().includes(term) ||
      (c.cidade || "").toLowerCase().includes(term) ||
      String(c.id_corrida || c.id || "").includes(term)
    );
  }, [corridas, globalFilter]);

  // Paginação
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina) || 1;
  const inicioIndice = (paginaAtual - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicioIndice, inicioIndice + itensPorPagina);
  const totalMostrado = dadosFiltrados.length;
  const inicioItem = totalMostrado > 0 ? inicioIndice + 1 : 0;
  const fimItem = Math.min(inicioIndice + itensPorPagina, totalMostrado);

  return (
    <div className="flex flex-col gap-6 w-full text-urb-text">
      <Toast ref={toastRef} />

      {/* Informações explicativas */}
      <div className="bg-urb-card border-l-4 border-urb-primary border-t border-r border-b border-urb-border rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-urb-primary/10 border border-urb-primary/20 flex items-center justify-center shrink-0">
            <DollarSign className="w-5 h-5 text-urb-primary" />
          </div>
          <div>
            <h3 className="text-white text-base font-bold">
              Gerenciamento de Taxas Urbrace (em Reais)
            </h3>
            <p className="text-sm text-slate-400 mt-0.5">
              Defina o valor em reais (R$) retido pela Urbrace para cada inscrição realizada na corrida.
            </p>
          </div>
        </div>
      </div>

      {/* Tabela de Corridas e Taxas */}
      <div className="bg-urb-card border border-urb-border rounded-2xl p-4 sm:p-6 shadow-sm">
        {/* Barra superior */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="relative flex items-center w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 pointer-events-none" />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setPaginaAtual(1);
              }}
              placeholder="Buscar corrida ou organizador..."
              className="w-full pl-9 pr-8 py-2 bg-urb-card-light border border-urb-border rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary/60 transition-colors"
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter("")}
                className="absolute right-2.5 text-slate-400 hover:text-white bg-transparent border-none cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={carregarDados}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 bg-urb-card-light hover:bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>Atualizar</span>
            </button>
          </div>
        </div>

        {/* Tabela de Dados */}
        <div className="overflow-x-auto rounded-xl border border-urb-border/50">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-urb-border bg-urb-card-light/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th className="py-3 px-3 w-16">ID</th>
                <th className="py-3 px-4">Corrida</th>
                <th className="py-3 px-4">Data do Evento</th>
                <th className="py-3 px-4">Organizador</th>
                <th className="py-3 px-4">Taxa por Inscrição</th>
                <th className="py-3 px-4 text-center">Status Corrida</th>
                <th className="py-3 px-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-urb-border/30">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-3 px-3"><div className="h-5 w-8 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-36 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-24 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-28 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-6 w-32 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-16 bg-slate-800 rounded mx-auto"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-7 w-24 bg-slate-800 rounded mx-auto"></div></td>
                  </tr>
                ))
              ) : dadosPaginados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-500" />
                    <p className="text-sm">Nenhuma corrida encontrada.</p>
                  </td>
                </tr>
              ) : (
                dadosPaginados.map((row) => (
                  <tr key={row.id_corrida || row.id} className="hover:bg-urb-card-light/40 transition-colors">
                    <td className="py-3 px-3 text-xs text-slate-500 font-mono">
                      #{row.id_corrida || row.id}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-white">
                          {row.nome_corrida || row.nome}
                        </span>
                        <span className="text-xs text-slate-400">
                          {row.cidade ? `${row.cidade} - ${row.estado || ""}` : "Local não informado"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 text-slate-300">
                        <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>{formatDate(row.data_evento || row.data)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      {row.organizador_nome || row.organizador || row.usuario_organizador || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-3 py-1 rounded-lg text-xs font-bold bg-urb-primary/10 text-urb-primary border border-urb-primary/30">
                        {getTaxaExibicao(row)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                          String(row.status_evento || row.status || "").toLowerCase().includes("abert")
                            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                            : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                        }`}
                      >
                        {row.status_evento || row.status || "Ativa"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        type="button"
                        onClick={() => abrirEdicaoTaxa(row)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-urb-card-light hover:bg-urb-card border border-urb-border hover:border-urb-primary/60 text-slate-200 hover:text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <Edit2 className="w-3.5 h-3.5 text-urb-primary" />
                        <span>Editar Taxa</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer com Paginação */}
        {!loading && dadosFiltrados.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 mt-3 border-t border-urb-border/40">
            <span className="text-xs text-slate-400">
              Mostrando {inicioItem} a {fimItem} de {totalMostrado} corridas
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  type="button"
                  onClick={() => setPaginaAtual(pg)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border ${
                    pg === paginaAtual
                      ? "bg-urb-primary text-black border-urb-primary"
                      : "bg-urb-card-light border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/40"
                  }`}
                >
                  {pg}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Configuração de Taxa em Real */}
      <Modal
        header={`Definir Taxa em Reais — ${corridaSelecionada?.nome_corrida || corridaSelecionada?.nome || "Corrida"}`}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2.5 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDialogVisible(false)}
              disabled={salvando}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={salvarTaxa}
              isLoading={salvando}
              className="gap-1.5 font-bold"
            >
              <Check className="w-4 h-4" />
              <span>Salvar Taxa</span>
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 text-urb-text">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
              Valor da Taxa por Inscrição (R$)
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-slate-400 font-semibold text-sm">R$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={valorTaxaReal}
                onChange={(e) => setValorTaxaReal(e.target.value)}
                placeholder="5.00"
                className="w-full pl-10 pr-3.5 py-2.5 bg-urb-card-light border border-urb-border rounded-xl text-sm font-semibold text-white focus:outline-none focus:border-urb-primary transition-colors"
                autoFocus
              />
            </div>
            <span className="text-[11px] text-slate-400 leading-relaxed">
              Valor fixo em Reais retido pela plataforma para cada inscrição realizada nesta corrida.
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
              Observações Internas (Staff)
            </label>
            <input
              type="text"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex: Taxa especial negociada para evento inaugural, etc."
              className="w-full px-3.5 py-2.5 bg-urb-card-light border border-urb-border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary transition-colors"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default StaffTaxas;
